import { BUTTON_SIZE, BUTTON_TYPE, DSBottomSheet, DSButton, DSModal } from '@repo/design-system/index'
import { Dispatch, SetStateAction, useState } from 'react'
import {
  AvailableCouponBundlesResponse,
  IApplyInfo,
  IPackage,
} from '../../../../modules/apis/student/lesson/schedule/types'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_book_lessons, n_common, n_weekly } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { formatDateWithDuration } from '../../../../modules/utils'
import { COLORS } from '../../../../modules/vars'
import CouponCard from './CouponCard'

const PackageSelectModalBody = ({
  applyInfo,
  urgent_coupons,
  selectedItem,
  duration,
  handleCouponClick,
  last_minute_coupons,
  packages,
}) => {
  const { t, isLocaleKO } = useTranslation()
  const { currentUser } = useCurrentUser()
  return (
    <div>
      <div className="Body2-14Md text-gray-700">{t(n_book_lessons.select_credit.time_booked)}</div>
      <div className="Title1-20Bd flex items-center">
        {formatDateWithDuration(applyInfo?.selectedTime?.classtime, duration, isLocaleKO)}
        {applyInfo?.selectedTime?.classtime?.is_today && (
          <div className="Body2-14Bd ml-[8px] text-red-500">{t(n_book_lessons.last_min_time)}</div>
        )}
        {formatDateWithDuration(applyInfo?.selectedTimeslot, duration, isLocaleKO)}
        {applyInfo?.selectedTimeslot?.is_today && (
          <div className="Body2-14Bd ml-[8px] text-red-500">{t(n_book_lessons.last_min_time)}</div>
        )}
      </div>
      <div className="Body2-14Md text-gray-500">{currentUser?.timezone}</div>
      <div className="mt-[16px] max-h-[500px] overflow-y-scroll">
        {urgent_coupons && (
          <CouponCard
            selected={selectedItem?.id == urgent_coupons.coupon_type_id}
            duration={duration}
            title={urgent_coupons.title}
            exp_dates={urgent_coupons.exp_dates}
            count={urgent_coupons.count}
            id={urgent_coupons.coupon_type_id}
            type="coupon_type"
            isFree={true}
            handleCouponClick={handleCouponClick}
            isAvailable={urgent_coupons.is_available}
          />
        )}
        {last_minute_coupons && (
          <CouponCard
            duration={duration}
            selected={selectedItem?.id == last_minute_coupons.coupon_type_id}
            title={last_minute_coupons.title}
            exp_dates={last_minute_coupons.exp_dates}
            count={last_minute_coupons.count}
            id={last_minute_coupons.coupon_type_id}
            isAvailable={last_minute_coupons.is_available}
            type="coupon_type"
            isFree={true}
            handleCouponClick={handleCouponClick}
          />
        )}
        {(urgent_coupons || last_minute_coupons) && <hr className="mt-[8px]" />}
        <div>
          {packages.map((coupon: IPackage, i) => (
            <CouponCard
              key={i}
              selected={selectedItem?.id == coupon.coupon_bundle_id}
              isFree={!coupon.is_paid}
              duration={duration}
              id={coupon.coupon_bundle_id}
              isAvailable={coupon.is_available}
              title={coupon.title}
              exp_dates={coupon.exp_dates}
              count={coupon.count}
              isWeeklyLessonPackage={coupon.is_weekly_lesson_package}
              type="coupon_bundle"
              handleCouponClick={handleCouponClick}
            />
          ))}
        </div>
        {packages.find((coupon) => coupon.is_weekly_lesson_package) && (
          <div className="mt-[8px] bg-[#FBFBFF] rounded-[8px] Body2-14Bd p-[16px]">{t(n_weekly.credit.caption)}</div>
        )}
      </div>
    </div>
  )
}
interface ICouponCard {
  id: number
  title: string
  exp_dates: number
  count: number
  duration: number
  isPaidForRookieLesson: boolean
  isWeeklyLessonPackage: boolean
}
const PackageSelectModal = ({
  showPackageSelectModal,
  setShowPackageSelectModal,
  applyInfo,
  setApplyInfo,
  data,
  onClose,
}: {
  showPackageSelectModal: boolean
  setShowPackageSelectModal: Dispatch<SetStateAction<boolean>>
  applyInfo: IApplyInfo
  setApplyInfo: Dispatch<SetStateAction<IApplyInfo>>
  data: AvailableCouponBundlesResponse
  onClose: () => void
}) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const { packages, urgent_coupons, last_minute_coupons } = data
  const [selectedItem, setSelectedItem] = useState<ICouponCard>()
  const [type, setType] = useState()
  const duration = applyInfo.duration

  const handleCouponClick = (e, type, item) => {
    e.preventDefault()
    setType(type)
    setSelectedItem(item)
  }

  const handleClick = () => {
    if (type == 'coupon_type') {
      setApplyInfo({
        ...applyInfo,
        selectedCoupon: {
          ...applyInfo.selectedCoupon,
          couponTypeId: selectedItem.id,
          isWeeklyLessonPackage: false,
        },
      })
    } else {
      setApplyInfo({
        ...applyInfo,
        selectedCoupon: {
          ...applyInfo.selectedCoupon,
          couponBundleId: selectedItem.id,
          isWeeklyLessonPackage: selectedItem.isWeeklyLessonPackage,
        },
      })
    }
    setShowPackageSelectModal(false)
  }

  return isMobile ? (
    <DSBottomSheet
      id="showPackageSelectModal"
      isOpen={showPackageSelectModal}
      setIsOpen={setShowPackageSelectModal}
      closeOnDim={true}
      closeButton={true}
      noDim={false}
    >
      <div className="p-[20px]">
        <div className="Title2-18Bd">{t(n_book_lessons.select_credit.title)}</div>
        <div className="mt-[20px]">
          <PackageSelectModalBody
            applyInfo={applyInfo}
            urgent_coupons={urgent_coupons}
            selectedItem={selectedItem}
            duration={duration}
            handleCouponClick={handleCouponClick}
            last_minute_coupons={last_minute_coupons}
            packages={packages}
          />
        </div>
        <div className="w-full">
          <DSButton disabled={!selectedItem} size={BUTTON_SIZE.LG} onClick={handleClick}>
            <DSButton.Label>{t(n_book_lessons.select_credit.btn_selected)}</DSButton.Label>
          </DSButton>
        </div>
      </div>
    </DSBottomSheet>
  ) : (
    <DSModal
      id="showPackageSelectModal"
      isOpen={showPackageSelectModal}
      setIsOpen={setShowPackageSelectModal}
      size="lg"
    >
      <DSModal.Title>{t(n_book_lessons.select_credit.title)}</DSModal.Title>
      <DSModal.Content className="max-h-[340px] md:max-h-none">
        <PackageSelectModalBody
          applyInfo={applyInfo}
          urgent_coupons={urgent_coupons}
          selectedItem={selectedItem}
          duration={duration}
          handleCouponClick={handleCouponClick}
          last_minute_coupons={last_minute_coupons}
          packages={packages}
        />
      </DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.OUTLINE} customBgColor={COLORS.WHITE} onClick={onClose}>
          <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
        </DSButton>
        <DSButton disabled={!selectedItem} size={BUTTON_SIZE.MD} onClick={handleClick}>
          <DSButton.Label>{t(n_book_lessons.select_credit.btn_selected)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

export default PackageSelectModal
